import clsx from 'clsx';
import type { Camera } from '@api/types';

interface Props {
  cameras: Camera[];
  selectedCameraId: number | undefined;
  onSelect: (cameraId: number | undefined) => void;
  isLoading?: boolean;
  error?: string | null;
}

export function HistoryCameraList({
  cameras,
  selectedCameraId,
  onSelect,
  isLoading = false,
  error = null,
}: Props): JSX.Element {
  const allActive = selectedCameraId === undefined;

  return (
    <aside
      className="flex w-56 shrink-0 flex-col border-r"
      style={{
        borderColor: 'var(--bui-border)',
        backgroundColor: 'var(--bui-surface)',
      }}
      aria-label="Camera filter"
    >
      <div
        className="border-b px-3 py-2.5"
        style={{ borderColor: 'var(--bui-divider)' }}
      >
        <h2 className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--bui-text-secondary)' }}>
          Cameras
        </h2>
      </div>

      <nav className="flex min-h-0 flex-1 flex-col gap-0.5 overflow-y-auto p-2">
        <CameraListItem
          label="All cameras"
          active={allActive}
          onClick={() => onSelect(undefined)}
        />

        {isLoading && cameras.length === 0 ? (
          <p className="px-2 py-3 text-xs" style={{ color: 'var(--bui-text-secondary)' }}>
            Loading cameras…
          </p>
        ) : null}

        {error ? (
          <p className="px-2 py-3 text-xs" style={{ color: 'var(--bui-danger)' }}>
            {error}
          </p>
        ) : null}

        {!isLoading && !error && cameras.length === 0 ? (
          <p className="px-2 py-3 text-xs" style={{ color: 'var(--bui-text-secondary)' }}>
            No cameras configured.
          </p>
        ) : null}

        {cameras.map((camera) => (
          <CameraListItem
            key={camera.id}
            label={camera.name}
            active={selectedCameraId === camera.id}
            onClick={() => onSelect(camera.id)}
          />
        ))}
      </nav>
    </aside>
  );
}

function CameraListItem({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      className={clsx(
        'w-full rounded-md px-2.5 py-2 text-left text-sm transition-colors',
        'focus-visible:outline-none focus-visible:ring-2',
      )}
      style={{
        color: active ? 'var(--bui-accent)' : 'var(--bui-text-primary)',
        backgroundColor: active ? 'var(--bui-surface-selected)' : 'transparent',
        fontWeight: active ? 600 : 400,
      }}
      onMouseEnter={(e) => {
        if (!active) e.currentTarget.style.backgroundColor = 'var(--bui-surface-hover)';
      }}
      onMouseLeave={(e) => {
        if (!active) e.currentTarget.style.backgroundColor = 'transparent';
      }}
    >
      <span className="block truncate" title={label}>
        {label}
      </span>
    </button>
  );
}
